<template>
  <Dialog v-model:open="isSearchOpened">
    <DialogContent
      class="gap-0 p-0 sm:max-w-2xl [&>button]:hidden"
      @pointer-down-outside.stop
      @escape-key-down.stop
    >
      <DialogHeader class="sr-only">
        <DialogTitle>Search</DialogTitle>
        <DialogDescription>
          Search for a stacklet, bucket, server, domain, firewall, kubernetes, or database.
        </DialogDescription>
      </DialogHeader>
      <div class="">
        <SearchForm
          ref="searchForm"
          v-model:query="query"
          :loading="loading"
          @close="isSearchOpened = false"
          @highlight:up="highlight('up')"
          @highlight:down="highlight('down')"
          @keydown.enter="handleKeydownEnter"
        />
        <main ref="container" class="flex-1 space-y-6 overflow-auto py-3">
          <template v-if="query && query.length >= 2">
            <ResultsSection
              v-if="results.length > 0"
              :results="results"
              :highlighted-index="highlightedIndex"
              @mouseover="(index) => (highlightedIndex = index)"
              @click="handleClick"
            />
            <div v-else-if="!loading" class="text-muted-foreground py-6 text-center">
              Nothing found for <span class="text-primary font-medium">"{{ query }}"</span>
            </div>
          </template>
          <template v-else>
            <HistorySection
              v-if="history.length > 0"
              :history="history"
              :highlighted-index="highlightedIndex"
              @mouseover="(index) => (highlightedIndex = index)"
              @click="handleClick"
            />
            <HistoryEmptySection v-else />
          </template>
        </main>
        <div class="border-t px-6 py-3 text-xs">
          <span class="font-medium uppercase">tip: </span>
          <span class="text-muted-foreground">Use keyboard arrows ⬆ and ⬇ to navigate.</span>
        </div>
      </div>
      <div tabindex="0" @focus.stop="searchForm?.focus()"></div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { debouncedRef } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, type Ref, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue-sonner';

import { useAppStore } from '@/app/store';
import { getServer } from '@/entities/baremetal';
import { getBucket } from '@/entities/bucket';
import { getDatabase } from '@/entities/database';
import { getDomain } from '@/entities/domain';
import { getFirewall } from '@/entities/firewall';
import { getKubernetes } from '@/entities/kubernetes';
import { type Results, search } from '@/entities/search';
import { getStacklet } from '@/entities/stacklet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

import SearchForm from './search-form.vue';
import HistorySection from './sections/history.vue';
import HistoryEmptySection from './sections/history-empty.vue';
import ResultsSection from './sections/results.vue';

const HISTORY_MAX_LENGTH = 5;
const HISTORY_STORAGE_KEY = '__search_history__';

const route = useRoute();
const router = useRouter();
const { isSearchOpened } = storeToRefs(useAppStore());

const searchForm = ref<InstanceType<typeof SearchForm>>();
const query = ref('');
const debouncedQuery = debouncedRef(query, 300);
const history = ref<Results[]>([]);
const highlightedIndex = ref(-1);
const container = ref() as Ref<HTMLElement>;

const { data, isFetching: loading } = useQuery({
  queryKey: ['search', debouncedQuery],
  placeholderData: keepPreviousData,
  queryFn: () => search({ query: debouncedQuery.value }),
  enabled: computed(() => debouncedQuery.value.length >= 2),
});

const results = computed(() => data.value?.results || []);
const isSearchMode = computed(() => query.value.length >= 2);

const getResultId = (item: Results) => {
  if ('id' in item) return `id:${item.id}`;
  if ('uuid' in item) return `uuid:${item.uuid}`;
  if ('name' in item) return `name:${item.name}`;
  return '';
};

const isSameResult = (item: Results, selected: Results) =>
  item.type === selected.type && getResultId(item) === getResultId(selected);

const setHistory = (items: Results[]) => {
  history.value = items;
  window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(items));
};

const writeHistory = (selected: Results) => {
  const nextHistory = [
    selected,
    ...history.value.filter((item) => !isSameResult(item, selected)),
  ].slice(0, HISTORY_MAX_LENGTH);

  setHistory(nextHistory);
};

const removeFromHistory = (selected: Results) => {
  const nextHistory = history.value.filter((item) => !isSameResult(item, selected));

  setHistory(nextHistory);
  highlightedIndex.value = -1;
};

const redirectToItemByType = (item: Results) => {
  switch (item.type) {
    case 'stacklet':
      router.push({
        name: 'stacklet/overview',
        params: { stacklet_id: item.id },
      });
      break;
    case 'bucket':
      router.push({
        name: 'bucket/files',
        params: { bucket_id: item.name },
      });
      break;
    case 'baremetal':
      router.push({
        name: 'server/overview',
        params: { server_id: item.id },
      });
      break;
    case 'domain':
      router.push({
        name: 'domain',
        params: { name: item.name },
      });
      break;
    case 'firewall':
      router.push({
        name: 'firewall/rules',
        params: { uuid: item.uuid },
      });
      break;
    case 'kubernetes':
      router.push({
        name: 'kubernetes/overview',
        params: { uuid: item.uuid },
      });
      break;
    case 'database':
      router.push({
        name: 'database/overview',
        params: { database_uuid: item.uuid },
      });
      break;
  }
};

const checkHistoryItemExists = async (item: Results) => {
  try {
    switch (item.type) {
      case 'stacklet':
        await getStacklet(item.id);
        break;
      case 'bucket':
        await getBucket(item.name);
        break;
      case 'baremetal':
        await getServer(item.id);
        break;
      case 'domain':
        await getDomain(item.name);
        break;
      case 'firewall':
        await getFirewall(item.uuid);
        break;
      case 'kubernetes':
        await getKubernetes(item.uuid);
        break;
      case 'database':
        await getDatabase(item.uuid);
        break;
    }

    return true;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;

    if (status === 404) {
      removeFromHistory(item);
      toast.error('Resource not found', {
        description: 'It was removed from your search history.',
      });
      return false;
    }

    return true;
  }
};

const handleSelection = async (item: Results, shouldCheckHistory = false) => {
  if (shouldCheckHistory) {
    const exists = await checkHistoryItemExists(item);
    if (!exists) return;
  }

  writeHistory(item);

  redirectToItemByType(item);

  isSearchOpened.value = false;
};

const handleKeydownEnter = async () => {
  if (highlightedIndex.value === -1) return;

  const arr = isSearchMode.value ? results.value : history.value;
  const item = arr[highlightedIndex.value];

  if (!item) return;

  await handleSelection(item, !isSearchMode.value);
};

const handleClick = async () => {
  const arr = isSearchMode.value ? results.value : history.value;
  const item = arr[highlightedIndex.value];

  if (!item) return;

  await handleSelection(item, !isSearchMode.value);
};

const highlight = (direction: 'down' | 'up') => {
  const arr = isSearchMode.value ? results.value : history.value;

  if (highlightedIndex.value === arr.length) return;

  if (direction === 'down') {
    highlightedIndex.value += 1;
    if (highlightedIndex.value === arr.length) {
      highlightedIndex.value = 0;
    }
  }

  if (direction === 'up') {
    highlightedIndex.value -= 1;
    if (highlightedIndex.value < 0) {
      highlightedIndex.value = arr.length - 1;
    }
  }

  nextTick(() => {
    const items = container.value?.querySelectorAll('li');
    if (items && items[highlightedIndex.value]) {
      items[highlightedIndex.value]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
};

watch(query, (string) => {
  if (string.length === 0) {
    highlightedIndex.value = -1;
    return;
  }

  if (string.length < 2) {
    highlightedIndex.value = -1;
    return;
  }
});

watch(
  () => route.path,
  () => {
    isSearchOpened.value = false;
  },
);

watch(
  isSearchOpened,
  (opened) => {
    if (opened) {
      const stored = window.localStorage.getItem(HISTORY_STORAGE_KEY);
      history.value = stored ? JSON.parse(stored) : [];
    } else {
      query.value = '';
      highlightedIndex.value = -1;
    }
  },
  { immediate: true },
);
</script>
